/**
 * L'AFFICHE — ce qui ne se fait que dans le navigateur : lire l'image déposée,
 * la recadrer sur un `canvas`, l'encoder (§9.1, §9.6).
 *
 * Les calculs (cadre, taille de sortie, refus, teinte, fond) sont dans
 * `affiche-model.ts`, testés hors navigateur ; ici, seulement le dessin.
 * L'image ne quitte jamais l'appareil avant l'envoi à Gestion : les adresses
 * sont des `blob:` et des `data:`, que la CSP autorise.
 */

import {
  POIDS_MAX_ENVOI, estTypeAffiche, octetsDepuisDataUrl, preparerFond, refusCadre, refusImage, refusType, tailleSortie, teinteDominante,
  type AfficheBrouillon, type Cadre, type TypeAffiche,
} from './affiche-model.js'

/** L'image d'origine, en mémoire le temps de la fenêtre (jamais rangée : seule l'affiche recadrée l'est). */
export interface SourceAffiche { url: string; img: HTMLImageElement; iw: number; ih: number; type: TypeAffiche }

export type Lu<T> = { ok: true; valeur: T } | { ok: false; message: string }

export async function lireSource(fichier: File): Promise<Lu<SourceAffiche>> {
  const type = fichier.type
  if (!estTypeAffiche(type)) return { ok: false, message: refusType(type) ?? 'L’affiche doit être une image JPEG, PNG ou WebP.' }
  const url = URL.createObjectURL(fichier)
  const img = new Image()
  img.decoding = 'async'
  img.src = url
  try { await img.decode() } catch {
    URL.revokeObjectURL(url)
    return { ok: false, message: 'Cette image ne se lit pas : choisissez-en une autre (JPEG, PNG ou WebP).' }
  }
  // L'orientation de l'appareil photo est déjà appliquée par le navigateur (`image-orientation: from-image`).
  const refus = refusImage(img.naturalWidth, img.naturalHeight)
  if (refus) { URL.revokeObjectURL(url); return { ok: false, message: refus } }
  return { ok: true, valeur: { url, img, iw: img.naturalWidth, ih: img.naturalHeight, type } }
}

export function libererSource(s: SourceAffiche | null): void {
  if (s) URL.revokeObjectURL(s.url)
}

function toile(l: number, h: number): { c: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  const c = document.createElement('canvas')
  c.width = l; c.height = h
  const ctx = c.getContext('2d')
  return ctx ? { c, ctx } : null
}

const versBlob = (c: HTMLCanvasElement, type: string, qualite?: number): Promise<Blob | null> =>
  new Promise((ok) => { c.toBlob((b) => ok(b), type, qualite) })

const versDataUrl = (b: Blob): Promise<string> =>
  new Promise((ok, ko) => {
    const r = new FileReader()
    r.onload = () => ok(String(r.result))
    r.onerror = () => ko(r.error)
    r.readAsDataURL(b)
  })

/**
 * Le recadrage : la zone choisie, en 16:9 exact (1 920 × 1 080 au plus,
 * jamais agrandie), encodée en WebP — ou, si le navigateur ne sait pas
 * l'écrire, en PNG pour une source qui peut être transparente, en JPEG sinon.
 * La transparence est gardée tant que le format le permet (aucun aplat).
 * Avec, pour l'aperçu du bandeau : la teinte dominante et un fond flouté.
 */
export async function recadrer(s: SourceAffiche, cadre: Cadre): Promise<Lu<AfficheBrouillon>> {
  const refus = refusCadre(cadre)
  if (refus) return { ok: false, message: refus }
  const { largeur, hauteur } = tailleSortie(cadre)
  const t = toile(largeur, hauteur)
  if (!t) return { ok: false, message: 'Ce navigateur ne sait pas recadrer l’image : essayez avec un autre.' }
  t.ctx.imageSmoothingEnabled = true
  t.ctx.imageSmoothingQuality = 'high'
  t.ctx.drawImage(s.img, cadre.x, cadre.y, cadre.w, cadre.h, 0, 0, largeur, hauteur)

  let blob = await versBlob(t.c, 'image/webp', 0.9)
  if (!blob || blob.type !== 'image/webp') blob = s.type === 'image/png' ? await versBlob(t.c, 'image/png') : await versBlob(t.c, 'image/jpeg', 0.9)
  if (blob && blob.size > POIDS_MAX_ENVOI) blob = await versBlob(t.c, 'image/jpeg', 0.85)
  if (!blob || !estTypeAffiche(blob.type)) return { ok: false, message: 'L’image n’a pas pu être préparée : essayez avec une autre.' }
  if (blob.size > POIDS_MAX_ENVOI) return { ok: false, message: 'L’affiche recadrée dépasse 8 Mio : choisissez une image moins chargée.' }

  // La teinte, sur une vignette ; le fond, sur une petite image floutée que le navigateur agrandit.
  let teinte: string | null = null
  const petite = toile(64, 36)
  if (petite) {
    petite.ctx.drawImage(t.c, 0, 0, 64, 36)
    teinte = teinteDominante(petite.ctx.getImageData(0, 0, 64, 36).data)
  }
  let fond = ''
  const flou = toile(128, 72)
  if (flou) {
    flou.ctx.drawImage(t.c, 0, 0, 128, 72)
    const px = flou.ctx.getImageData(0, 0, 128, 72)
    preparerFond(px.data, 128, 72)
    flou.ctx.putImageData(px, 0, 0)
    fond = flou.c.toDataURL('image/jpeg', 0.82)
  }
  try {
    const dataUrl = await versDataUrl(blob)
    return { ok: true, valeur: { data_url: dataUrl, fond_url: fond.startsWith('data:image/') ? fond : dataUrl, type: blob.type, largeur, hauteur, teinte } }
  } catch {
    return { ok: false, message: 'L’image n’a pas pu être préparée : essayez avec une autre.' }
  }
}

/** Le fichier envoyé à Gestion (§9.5) : les octets de l'affiche recadrée, avec leur type. */
export function fichierAffiche(a: AfficheBrouillon): File | null {
  const o = octetsDepuisDataUrl(a.data_url)
  if (!o) return null
  const ext = o.type === 'image/png' ? 'png' : o.type === 'image/webp' ? 'webp' : 'jpg'
  return new File([o.octets], `affiche.${ext}`, { type: o.type })
}
